import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Shared production-unit picker state for L1/L2/QC batch forms.
 *
 * - Fetches all active production units once.
 * - Auto-selects the only unit when exactly one is active (single-unit shops
 *   should never see a dropdown).
 * - Returns:
 *     units        → [{value, label}]
 *     unitId       → currently selected id (or null)
 *     setUnitId    → setter for the picker
 *     required     → true when the picker must be shown (>1 active units)
 *     loading      → initial fetch state
 */
export default function useProductionUnit(initialId = null) {
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState(initialId);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/production/units").then((r) => {
      if (cancelled) return;
      const active = (r.data || []).filter((u) => u.is_active !== false);
      const opts = active.map((u) => ({ value: u.id, label: u.name }));
      setUnits(opts);
      // Auto-select the single available unit only when the caller hasn't
      // already restored an id (edit mode preserves the saved unit).
      if (!initialId && active.length === 1) setUnitId(active[0].id);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    units,
    unitId,
    setUnitId,
    required: units.length > 1,   // hide picker for single-unit shops
    loading,
  };
}
